import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, Key, RefreshCw } from "lucide-react";

export const ApiKeyManager = () => {
  const { toast } = useToast();
  const [isRotating, setIsRotating] = useState(false);

  const { data: apiKeys, isLoading, refetch } = useQuery({
    queryKey: ['api-keys'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('api_keys')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data;
    },
  });

  const rotateMaskedKeysMutation = useMutation({
    mutationFn: async () => {
      if (!apiKeys?.length) return { rotated_keys: 0 };

      // Only update the masked_key_value while keeping all other fields unchanged
      const { data, error } = await supabase
        .from('api_keys')
        .update({ 
          masked_key_value: `mk_${Math.random().toString(36).substring(2, 15)}`
        })
        .in('id', apiKeys.map(key => key.id))
        .select();

      if (error) throw error;
      return { rotated_keys: data.length };
    },
    onSuccess: (data) => {
      toast({
        title: "Keys Masked Successfully",
        description: `${data.rotated_keys} keys were re-masked.`
      });
      refetch();
    },
    onError: (error) => {
      toast({
        variant: "destructive",
        title: "Error Rotating Keys",
        description: error.message
      });
    },
    onSettled: () => {
      setIsRotating(false);
    }
  });

  const handleRotateKeys = async () => {
    setIsRotating(true);
    rotateMaskedKeysMutation.mutate();
  };

  if (isLoading) {
    return <div>Loading API keys...</div>;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Key className="h-5 w-5" />
            API Key Management
          </div>
          <Button
            onClick={handleRotateKeys}
            disabled={isRotating}
            variant="outline"
            size="sm"
          >
            {isRotating ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Rotating Keys...
              </>
            ) : (
              <>
                <RefreshCw className="mr-2 h-4 w-4" />
                Rotate Keys
              </>
            )}
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {apiKeys?.map((key) => (
            <div
              key={key.id}
              className="flex items-center justify-between border-b pb-2"
            >
              <div>
                <p className="font-medium">{key.key_name}</p>
                <p className="text-sm text-muted-foreground font-mono">
                  {key.masked_key_value}
                </p>
                <p className="text-sm text-muted-foreground">
                  Last rotated: {new Date(key.last_rotated).toLocaleDateString()}
                </p>
              </div>
              <Badge variant={key.is_active ? "default" : "secondary"}>
                {key.is_active ? "Active" : "Inactive"}
              </Badge>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};
